import React, { useState, useEffect, useRef } from 'react';
import { Nav, Tab, Table, Badge, Alert } from 'react-bootstrap';
import { motion } from 'framer-motion';
import api from '../services/api';

const pageVariants = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -10 }
};

const containerVariants = {
  animate: { transition: { staggerChildren: 0.08 } }
};

const itemVariants = {
  initial: { opacity: 0, y: 16 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.35 } }
};

function Transactions() {
  const [activeTab, setActiveTab] = useState('summary');
  const [summary, setSummary] = useState(null);
  const [subscriptions, setSubscriptions] = useState([]);
  const [tips, setTips] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [syncNotice, setSyncNotice] = useState('');
  const [syncingStripe, setSyncingStripe] = useState(false);
  const [refundingId, setRefundingId] = useState(null);
  const subscriptionRevenueWarmupDone = useRef(false);

  useEffect(() => {
    fetchSummary();
  }, []);

  useEffect(() => {
    if (activeTab === 'summary') {
      fetchSummary();
    } else if (activeTab === 'subscriptions') {
      fetchSubscriptions();
      const includeInvoiceRevenue = !subscriptionRevenueWarmupDone.current;
      if (includeInvoiceRevenue) {
        subscriptionRevenueWarmupDone.current = true;
      }
      handleStripeSync(true, false, includeInvoiceRevenue);
    } else if (activeTab === 'tips') {
      fetchTips();
    } else if (activeTab === 'withdrawals') {
      fetchWithdrawals();
    }
  }, [activeTab]);

  useEffect(() => {
    if (activeTab !== 'subscriptions') return undefined;

    const interval = setInterval(() => {
      handleStripeSync(true, false, false);
    }, 30000);

    return () => clearInterval(interval);
  }, [activeTab]);

  const fetchSummary = async () => {
    try {
      const response = await api.get('/admin/transactions/summary');
      setSummary(response.data);
    } catch (err) {
      setError('Failed to load transaction summary');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchSubscriptions = async () => {
    try {
      const response = await api.get('/admin/transactions/subscriptions');
      setSubscriptions(response.data.subscriptions);
    } catch (err) {
      console.error(err);
    }
  };

  const handleStripeSync = async (silent = false, fullScan = false, includeInvoiceRevenue = false) => {
    if (syncingStripe) return;
    setSyncingStripe(true);
    setError('');
    if (!silent) setSyncNotice('');
    try {
      const response = await api.post(
        '/admin/transactions/subscriptions/sync-stripe',
        { fullScan, includeInvoiceRevenue: includeInvoiceRevenue || fullScan },
        { timeout: fullScan ? 180000 : 45000 }
      );
      await fetchSubscriptions();
      await fetchSummary();
      const {
        usersUpdated = 0,
        subscriptionsUpserted = 0,
        usersMatchedByEmail = 0,
        unmatchedSubscriptions = 0,
        refundedSubscriptionsDetected = 0,
        invoiceEventsUpserted = 0
      } = response.data || {};

      if (!silent) {
        setSyncNotice(
          `Stripe ${fullScan ? 'full' : 'incremental'} sync complete: ${usersUpdated} users updated, ${subscriptionsUpserted} subscriptions synced, ${usersMatchedByEmail} email matches, ${unmatchedSubscriptions} unmatched, ${refundedSubscriptionsDetected} refunded downgraded, ${invoiceEventsUpserted} subscription payment rows merged for revenue.`
        );
      }
    } catch (err) {
      console.error(err);
      const detailedMessage =
        err.response?.data?.message ||
        err.response?.data?.error ||
        (err.code === 'ECONNABORTED'
          ? 'Stripe sync timed out. Please try again in a moment.'
          : err.message);
      const normalizedDetail = String(detailedMessage || '').trim();
      if (!normalizedDetail) {
        setError('Failed to sync subscriptions from Stripe');
      } else if (normalizedDetail.toLowerCase() === 'failed to sync subscriptions from stripe') {
        setError(normalizedDetail);
      } else {
        setError(`Failed to sync subscriptions from Stripe: ${normalizedDetail}`);
      }
    } finally {
      setSyncingStripe(false);
    }
  };

  const handleRefund = async (stripeSubscriptionId) => {
    const confirmed = window.confirm('Refund latest payment for this subscription and stop renewal?');
    if (!confirmed) return;

    setRefundingId(stripeSubscriptionId);
    setError('');
    try {
      await api.post(`/admin/transactions/subscriptions/${encodeURIComponent(stripeSubscriptionId)}/refund`, {
        reason: 'requested_by_customer'
      });
      await fetchSubscriptions();
      await fetchSummary();
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.message || 'Failed to create refund');
    } finally {
      setRefundingId(null);
    }
  };

  const fetchTips = async () => {
    try {
      const response = await api.get('/admin/transactions/tips');
      setTips(response.data.tips);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchWithdrawals = async () => {
    try {
      const response = await api.get('/admin/transactions/withdrawals');
      setWithdrawals(response.data.withdrawals);
    } catch (err) {
      console.error(err);
    }
  };

  const formatCurrency = (cents) => {
    return `$${(cents / 100).toFixed(2)}`;
  };

  if (loading && !summary) {
    return (
      <div className="page-loading">
        <div className="loading-spinner" />
      </div>
    );
  }

  return (
    <motion.div
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      transition={{ duration: 0.4, ease: [0.25, 0.1, 0.25, 1] }}
    >
      <div className="page-header">
        <h1 className="page-title">Transactions</h1>
        <p className="page-subtitle">Subscriptions, tips, and withdrawal history</p>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}
      {syncNotice && <Alert variant="success">{syncNotice}</Alert>}

      <Tab.Container activeKey={activeTab} onSelect={(k) => setActiveTab(k)}>
        <Nav variant="tabs" style={{ marginBottom: '24px' }}>
          <Nav.Item>
            <Nav.Link eventKey="summary">Summary</Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="subscriptions">Subscriptions</Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="tips">Tips</Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="withdrawals">Withdrawals</Nav.Link>
          </Nav.Item>
        </Nav>

        <Tab.Content>
          {/* Summary Tab */}
          <Tab.Pane eventKey="summary">
            {summary && (
              <motion.div
                className="bento-grid"
                style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}
                variants={containerVariants}
                initial="initial"
                animate="animate"
              >
                <motion.div className="glass-card" variants={itemVariants}>
                  <div className="card-header-row">
                    <h3 className="card-title">Subscriptions</h3>
                  </div>
                  <div className="data-pair">
                    <span className="data-pair-label">Total</span>
                    <span className="data-pair-value">{summary.subscriptions.total}</span>
                  </div>
                  <div className="data-pair">
                    <span className="data-pair-label">Active</span>
                    <span className="data-pair-value" style={{ color: 'var(--success)' }}>{summary.subscriptions.active}</span>
                  </div>
                  <div className="data-pair">
                    <span className="data-pair-label">Canceled</span>
                    <span className="data-pair-value">{summary.subscriptions.canceled}</span>
                  </div>
                  <div className="data-pair">
                    <span className="data-pair-label">Past Due</span>
                    <span className="data-pair-value" style={{ color: 'var(--warning)' }}>{summary.subscriptions.past_due}</span>
                  </div>
                  <div className="data-pair">
                    <span className="data-pair-label">Revenue</span>
                    <span className="data-pair-value">{formatCurrency(summary.subscriptionRevenueCents || 0)}</span>
                  </div>
                  <div className="data-pair">
                    <span className="data-pair-label">Refunded</span>
                    <span className="data-pair-value" style={{ color: 'var(--danger)' }}>
                      {formatCurrency(summary.refundedRevenueCents || 0)}
                    </span>
                  </div>
                  <div className="data-pair">
                    <span className="data-pair-label">Net Revenue</span>
                    <span className="data-pair-value" style={{ color: 'var(--success)' }}>
                      {formatCurrency(summary.netSubscriptionRevenueCents || 0)}
                    </span>
                  </div>
                </motion.div>

                <motion.div className="glass-card" variants={itemVariants}>
                  <div className="card-header-row">
                    <h3 className="card-title">Tips</h3>
                  </div>
                  <div className="data-pair">
                    <span className="data-pair-label">Total</span>
                    <span className="data-pair-value">{summary.tips.total}</span>
                  </div>
                  <div className="data-pair">
                    <span className="data-pair-label">Collected (charged)</span>
                    <span className="data-pair-value" style={{ color: 'var(--success)' }}>
                      {formatCurrency(summary.tips.succeeded_amount_cents ?? summary.tips.total_amount_cents ?? 0)}
                    </span>
                  </div>
                  <div className="data-pair">
                    <span className="data-pair-label">Pending checkout</span>
                    <span className="data-pair-value" style={{ color: 'var(--warning)' }}>
                      {formatCurrency(summary.tips.pending_amount_cents || 0)}
                    </span>
                  </div>
                  <div className="data-pair">
                    <span className="data-pair-label">Succeeded</span>
                    <span className="data-pair-value" style={{ color: 'var(--success)' }}>{summary.tips.succeeded}</span>
                  </div>
                  <div className="data-pair">
                    <span className="data-pair-label">Pending</span>
                    <span className="data-pair-value" style={{ color: 'var(--warning)' }}>{summary.tips.pending}</span>
                  </div>
                  <div className="data-pair">
                    <span className="data-pair-label">Failed</span>
                    <span className="data-pair-value" style={{ color: 'var(--danger)' }}>{summary.tips.failed}</span>
                  </div>
                  <div className="data-pair">
                    <span className="data-pair-label">Canceled</span>
                    <span className="data-pair-value">{summary.tips.canceled ?? 0}</span>
                  </div>
                </motion.div>

                <motion.div className="glass-card" variants={itemVariants}>
                  <div className="card-header-row">
                    <h3 className="card-title">Withdrawals</h3>
                  </div>
                  <div className="data-pair">
                    <span className="data-pair-label">Total</span>
                    <span className="data-pair-value">{summary.withdrawals.total}</span>
                  </div>
                  <div className="data-pair">
                    <span className="data-pair-label">Total Amount</span>
                    <span className="data-pair-value">{formatCurrency(summary.withdrawals.total_amount_cents || 0)}</span>
                  </div>
                  <div className="data-pair">
                    <span className="data-pair-label">Completed</span>
                    <span className="data-pair-value" style={{ color: 'var(--success)' }}>{summary.withdrawals.completed}</span>
                  </div>
                  <div className="data-pair">
                    <span className="data-pair-label">Pending</span>
                    <span className="data-pair-value" style={{ color: 'var(--warning)' }}>{summary.withdrawals.pending}</span>
                  </div>
                </motion.div>
              </motion.div>
            )}
          </Tab.Pane>

          {/* Subscriptions Tab */}
          <Tab.Pane eventKey="subscriptions">
            <div className="glass-card" style={{ padding: 0 }}>
              <div style={{ padding: '18px 24px', borderBottom: '1px solid var(--border-light)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h3 className="card-title" style={{ margin: 0 }}>Subscriptions</h3>
                <button
                  className="btn btn-sm btn-outline-warning"
                  onClick={() => handleStripeSync(false, true)}
                  disabled={syncingStripe}
                  title="Force full historical refresh from Stripe"
                >
                  {syncingStripe ? 'Syncing...' : 'Stripe Sync'}
                </button>
              </div>
              <div style={{ padding: '0 8px 8px', overflowX: 'auto' }}>
                <Table responsive>
                  <thead>
                    <tr>
                      <th>User</th>
                      <th>Stripe ID</th>
                      <th>Status</th>
                      <th>Start</th>
                      <th>End</th>
                      <th>Created</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {subscriptions.map((sub) => (
                      <tr key={sub.id}>
                        <td>
                          <div style={{ fontWeight: 500 }}>{sub.first_name} {sub.last_name}</div>
                          <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{sub.email}</div>
                        </td>
                        <td style={{ fontSize: '12px', fontFamily: 'monospace' }}>{sub.stripe_subscription_id}</td>
                        <td><Badge bg={sub.status === 'active' ? 'success' : 'secondary'}>{sub.status}</Badge></td>
                        <td>{new Date(sub.current_period_start).toLocaleDateString()}</td>
                        <td>{new Date(sub.current_period_end).toLocaleDateString()}</td>
                        <td>{new Date(sub.created_at).toLocaleDateString()}</td>
                        <td>
                          <button
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => handleRefund(sub.stripe_subscription_id)}
                            disabled={refundingId === sub.stripe_subscription_id}
                          >
                            {refundingId === sub.stripe_subscription_id ? 'Processing...' : 'Refund'}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </div>
          </Tab.Pane>

          {/* Tips Tab */}
          <Tab.Pane eventKey="tips">
            <div className="glass-card" style={{ padding: 0 }}>
              <div style={{ padding: '18px 24px', borderBottom: '1px solid var(--border-light)' }}>
                <h3 className="card-title" style={{ margin: 0 }}>Tips</h3>
              </div>
              <div style={{ padding: '0 8px 8px', overflowX: 'auto' }}>
                <Table responsive>
                  <thead>
                    <tr>
                      <th>From</th>
                      <th>To</th>
                      <th>Amount</th>
                      <th>Status</th>
                      <th>Message</th>
                      <th>Created</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tips.map((tip) => (
                      <tr key={tip.id}>
                        <td>
                          <div style={{ fontWeight: 500 }}>{tip.from_first_name} {tip.from_last_name}</div>
                          <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{tip.from_email}</div>
                        </td>
                        <td>
                          {tip.to_first_name ? (
                            <>
                              <div style={{ fontWeight: 500 }}>{tip.to_first_name} {tip.to_last_name}</div>
                              <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{tip.to_email}</div>
                            </>
                          ) : (
                            <span style={{ color: 'var(--text-muted)' }}>{tip.to_shadchan_email || 'N/A'}</span>
                          )}
                        </td>
                        <td style={{ fontWeight: 600 }}>{formatCurrency(tip.amount_cents)}</td>
                        <td>
                          <Badge bg={
                            tip.status === 'succeeded' ? 'success' :
                            tip.status === 'pending' ? 'warning' : 'danger'
                          }>
                            {tip.status}
                          </Badge>
                        </td>
                        <td style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {tip.message || <span style={{ color: 'var(--text-muted)' }}>—</span>}
                        </td>
                        <td>{new Date(tip.created_at).toLocaleDateString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </div>
          </Tab.Pane>

          {/* Withdrawals Tab */}
          <Tab.Pane eventKey="withdrawals">
            <div className="glass-card" style={{ padding: 0 }}>
              <div style={{ padding: '18px 24px', borderBottom: '1px solid var(--border-light)' }}>
                <h3 className="card-title" style={{ margin: 0 }}>Withdrawals</h3>
              </div>
              <div style={{ padding: '0 8px 8px', overflowX: 'auto' }}>
                <Table responsive>
                  <thead>
                    <tr>
                      <th>Shadchan</th>
                      <th>Method</th>
                      <th>Amount</th>
                      <th>Status</th>
                      <th>Created</th>
                      <th>Processed</th>
                    </tr>
                  </thead>
                  <tbody>
                    {withdrawals.map((withdrawal) => (
                      <tr key={withdrawal.id}>
                        <td>
                          <div style={{ fontWeight: 500 }}>{withdrawal.first_name} {withdrawal.last_name}</div>
                          <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{withdrawal.email}</div>
                        </td>
                        <td>{withdrawal.method_type}</td>
                        <td style={{ fontWeight: 600 }}>{formatCurrency(withdrawal.amount_cents)}</td>
                        <td>
                          <Badge bg={
                            withdrawal.status === 'completed' ? 'success' :
                            withdrawal.status === 'pending' ? 'warning' : 'danger'
                          }>
                            {withdrawal.status}
                          </Badge>
                        </td>
                        <td>{new Date(withdrawal.created_at).toLocaleDateString()}</td>
                        <td>
                          {withdrawal.processed_at
                            ? new Date(withdrawal.processed_at).toLocaleDateString()
                            : <span style={{ color: 'var(--text-muted)' }}>—</span>
                          }
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </div>
          </Tab.Pane>
        </Tab.Content>
      </Tab.Container>
    </motion.div>
  );
}

export default Transactions;
